/**
 * Sprint Process Handlers
 *
 * All sprint and training sprint related handlers
 */

import { handleRequest } from './config'
import { ApiResponse } from '../types'
import { TrainingSprint, TrainingSprintStatus } from '../../types/TrainingSprintTypes'
import { State } from '../../hooks/useAppState'
import { Sprint, type SprintLaunchOptions } from '../../../../pure-js/types/Sprint'
import { buildSprintTeamDisplayBuckets } from '../../../../pure-js/libs/buildSprintTeamDisplayBuckets'
import { SprintResponse, SprintsResponse } from '../../../../pure-js/types/SprintQueries'
import { directDb } from '../handlers'
import { loadCloudFunctionsHandler } from '../handlers'
import {
  transformTrainingSprintToBE,
  transformBEToTrainingSprintConfig
} from '../../features/facilitator/facilitatorTrainingSprintCreation/transformers'
import { TrainingSprintConfig } from '../../features/facilitator/facilitatorTrainingSprintCreation/types'

const unwrapSprintResponse = (res: ApiResponse<SprintResponse>): SprintResponse => {
  if (!res.success) {
    throw new Error(res.error ?? 'Sprint request failed')
  }
  return res.data
}

const unwrapSprintsResponse = (res: ApiResponse<SprintsResponse>): SprintsResponse => {
  if (!res.success) {
    throw new Error(res.error ?? 'Sprint request failed')
  }
  return res.data
}

// ========================================
// TRAINING SPRINT HANDLERS (Frontend)
// ========================================

/**
 * Get all training sprints with optional filters
 * Transforms backend Sprint[] to frontend TrainingSprint[]
 */
const transformSprintToTrainingSprint = (sprint: Sprint): TrainingSprint => {
  // Calculate progress based on completed activities
  const activities = sprint.activities || []
  const now = new Date()
  let completedActivities = 0

  activities.forEach((activity) => {
    const activityEndDate = new Date(activity.endDate)
    // Activity is completed if its end time has passed
    if (activityEndDate < now) {
      completedActivities++
    }
  })

  const progressPercent = activities.length > 0 ? Math.round((completedActivities / activities.length) * 100) : 0

  // Calculate duration from start and end dates
  let duration = '2 weeks' // Default fallback
  if (sprint.startDate && sprint.endDate) {
    const start = new Date(sprint.startDate)
    const end = new Date(sprint.endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const diffWeeks = Math.floor(diffDays / 7)

    if (diffWeeks > 0) {
      duration = `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'}`
    } else {
      duration = `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`
    }
  }

  const teams = sprint.teams || []
  const participants = sprint.participants || []
  const { teamDetails, unassignedMemberNames } = buildSprintTeamDisplayBuckets({ teams, participants })

  return {
    id: sprint.id,
    title: sprint.title || 'Untitled Training Sprint',
    status:
      sprint.status === 'active'
        ? TrainingSprintStatus.ACTIVE
        : sprint.status === 'inactive'
          ? TrainingSprintStatus.INACTIVE
          : sprint.status === 'completed'
            ? TrainingSprintStatus.COMPLETED
            : TrainingSprintStatus.INACTIVE,
    startDate: sprint.startDate || '',
    endDate: sprint.endDate || '',
    participants: (sprint.participants || []).length,
    teams: (sprint.teams || []).length,
    sessions: activities.length,
    createdAt: sprint.createdAt || new Date().toISOString(),
    updatedAt: sprint.createdAt || new Date().toISOString(),
    progress: progressPercent,
    duration,
    teamDetails,
    unassignedMemberNames,
    facilitatedByUserIds: sprint.facilitatedByUserIds ?? []
  }
}

export const getTrainingSprints = async (
  filters?: Record<string, string | number | boolean>,
  state?: State
): Promise<ApiResponse<TrainingSprint[]>> => {
  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    const stateToUse = state ?? ({ user: { id: '' } } as State)
    const sprintsResponse = await cloudFunctions.getSprints(stateToUse)
    const sprints = sprintsResponse.data?.sprints ?? []

    const transformedSprints: TrainingSprint[] = sprints.map(transformSprintToTrainingSprint)

    // Apply filters
    let filteredSprints = transformedSprints
    if (filters?.search && typeof filters.search === 'string') {
      const searchTerm = filters.search.toLowerCase()
      filteredSprints = filteredSprints.filter((sprint) => sprint.title.toLowerCase().includes(searchTerm))
    }

    if (filters?.status) {
      filteredSprints = filteredSprints.filter((sprint) => sprint.status === filters.status)
    }

    return {
      data: filteredSprints,
      success: true
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      data: [],
      success: false,
      error: message || 'Failed to fetch training sprints'
    }
  }
}

export type TrainingSprintsPageData = {
  sprints: TrainingSprint[]
  activeSprints: TrainingSprint[]
  total: number
  hasMore: boolean
  nextOffset: number
  nextCursor?: string
}

/**
 * Offset-paged training sprints for infinite scroll (facilitator management page).
 * Search runs server-side; the first page also carries every ACTIVE sprint.
 */
export const getTrainingSprintsPage = async (
  state: State,
  { limit = 20, offset = 0, search, cursor }: { limit?: number; offset?: number; search?: string; cursor?: string } = {}
): Promise<ApiResponse<TrainingSprintsPageData>> => {
  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    const response = await cloudFunctions.getSprintsPaged(state, { limit, offset, search, cursor })
    if (!response.success) {
      throw new Error(response.error ?? 'Failed to fetch training sprints')
    }
    const { sprints, activeSprints, total, hasMore, nextOffset, nextCursor } = response.data
    return {
      success: true,
      data: {
        sprints: sprints.map(transformSprintToTrainingSprint),
        activeSprints: activeSprints.map(transformSprintToTrainingSprint),
        total,
        hasMore,
        nextOffset,
        nextCursor
      }
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error)
    return {
      success: false,
      data: { sprints: [], activeSprints: [], total: 0, hasMore: false, nextOffset: offset },
      error: message || 'Failed to fetch training sprints'
    }
  }
}

/**
 * Get a single training sprint by ID
 */
export const getTrainingSprint = async (_id: string): Promise<ApiResponse<TrainingSprint | null>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: null,
        success: false,
        error: 'Training sprint not found'
      } as ApiResponse<TrainingSprint | null>),
    async () => {
      // TODO: Implement cloud function
      return { data: null, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Get all active training sprints
 */
export const getActiveTrainingSprints = async (): Promise<ApiResponse<TrainingSprint[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: [], success: true } as ApiResponse<TrainingSprint[]>),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

/**
 * Get all upcoming training sprints
 */
export const getUpcomingTrainingSprints = async (): Promise<ApiResponse<TrainingSprint[]>> => {
  return handleRequest(
    () => Promise.resolve({ data: [], success: true } as ApiResponse<TrainingSprint[]>),
    async () => {
      // TODO: Implement cloud function
      return { data: [], success: true }
    }
  )
}

// ========================================
// BACKEND SPRINT HANDLERS (Direct)
// ========================================

/**
 * Get a single sprint by ID from backend
 */
export const getSprint = async (sprintId: string, state: State): Promise<SprintResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintResponse(await cf.getSprint(sprintId, state))
}

/**
 * Get all sprints from backend
 */
export const getSprints = async (state: State, options?: { search?: string }): Promise<SprintsResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintsResponse(await cf.getSprints(state, options))
}

/** Resolve specific sprints by id — never a page the wanted sprint might fall outside of. */
export const getSprintsByIds = async (state: State, sprintIds: string[]): Promise<SprintsResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintsResponse(await cf.getSprintsByIds(state, { sprintIds }))
}

// ========================================
// SPRINT MUTATIONS (With Transformation)
// ========================================

/**
 * Create a draft sprint from frontend config
 * Transforms TrainingSprintConfig to backend Sprint format
 */
export const createDraftSprintFromConfig = async (
  sprintConfig: TrainingSprintConfig,
  state: State
): Promise<SprintResponse> => {
  const backendSprintData = transformTrainingSprintToBE(sprintConfig)
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintResponse(await cf.createDraftSprint(backendSprintData, state))
}

/**
 * Update a sprint from frontend config, and launch it in the same call when `launch` is given
 * Transforms TrainingSprintConfig to backend Sprint format
 */
export const updateSprintFromConfig = async (
  sprintId: string,
  sprintConfig: TrainingSprintConfig,
  state: State,
  launch?: SprintLaunchOptions
): Promise<SprintResponse> => {
  const backendSprintData = transformTrainingSprintToBE(sprintConfig)
  const cf = await loadCloudFunctionsHandler()
  const response = await cf.updateSprint(sprintId, backendSprintData, state, launch)
  return unwrapSprintResponse(response)
}

// ========================================
// SPRINT MUTATIONS (Direct Backend Types)
// ========================================

/**
 * Create a draft sprint with backend types
 * Direct passthrough to CloudFunctionsApiHandler
 */
export const createDraftSprint = async (sprintData: Partial<Sprint>, state: State): Promise<SprintResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintResponse(await cf.createDraftSprint(sprintData, state))
}

/**
 * Update a sprint with backend types
 * Direct passthrough to CloudFunctionsApiHandler
 */
export const updateSprint = async (
  sprintId: string,
  sprintData: Partial<Sprint>,
  state: State
): Promise<SprintResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintResponse(await cf.updateSprint(sprintId, sprintData, state))
}

/**
 * Delete a sprint (draft or active)
 */
export const deleteSprint = async (sprintId: string, state: State): Promise<SprintResponse> => {
  const cf = await loadCloudFunctionsHandler()
  return unwrapSprintResponse(await cf.deleteSprint(sprintId, state))
}

/**
 * Delete a draft sprint
 * Direct passthrough to CloudFunctionsApiHandler
 */

/**
 * Helper to load and transform sprint for editing
 * Fetches backend sprint and transforms to frontend config
 */
export const getSprintAsConfig = async (sprintId: string, state: State): Promise<TrainingSprintConfig | null> => {
  const response = await getSprint(sprintId, state)
  if (response.sprint) {
    return transformBEToTrainingSprintConfig(response.sprint)
  }
  return null
}

// ========================================
// LEGACY TRAINING SPRINT HANDLERS (Deprecated)
// ========================================

/**
 * @deprecated Use createDraftSprint instead
 */
export const createTrainingSprint = async (
  sprintData: Record<string, unknown>
): Promise<ApiResponse<Record<string, unknown>>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: { ...sprintData, id: 'new-sprint-id' },
        success: true
      } as ApiResponse<Record<string, unknown>>),
    async () => {
      // TODO: Implement cloud function
      return { data: {}, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * @deprecated Use updateSprint instead
 */
export const updateTrainingSprint = async (
  id: string,
  sprintData: Record<string, unknown>
): Promise<ApiResponse<Record<string, unknown>>> => {
  return handleRequest(
    () =>
      Promise.resolve({
        data: { ...sprintData, id },
        success: true
      } as ApiResponse<Record<string, unknown>>),
    async () => {
      // TODO: Implement cloud function
      return { data: {}, success: false, error: 'Not implemented' }
    }
  )
}

/**
 * Delete a training sprint
 */
export const deleteTrainingSprint = async (_id: string): Promise<ApiResponse<void>> => {
  return handleRequest(
    () => Promise.resolve({ data: undefined, success: true } as ApiResponse<void>),
    async () => {
      // TODO: Implement cloud function
      return { data: undefined, success: false, error: 'Not implemented' }
    }
  )
}

export const observeSprintLaunch = directDb.observeSprintLaunch
export type { SprintLaunchSnapshot } from '../handlers/directDbHandler'
